import { useEffect, useState } from "react";
import { getWeekStartString } from "@/lib/week";
import { resolveDayStatus, DAY_STATUS_KEYS } from "@/lib/presence";
import { useAlternanceJours } from "@/hooks/use-alternance";
import { Link } from "wouter";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { authFetch } from "@/lib/authFetch";
import { useMissions } from "@/hooks/use-missions";
import { useRobots, useResellers } from "@/hooks/use-inventory";
import { useTickets } from "@/hooks/use-tickets";
import { useTasks } from "@/hooks/use-tasks";
import { useSavDossiers } from "@/hooks/use-sav";
import { useTechnicianSchedules, useTechnicians, useCommercials } from "@/hooks/use-profiles";
import { useClients } from "@/hooks/use-clients";
import { useProspects } from "@/hooks/use-prospects";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusBadge } from "@/components/status-badge";
import {
  format, startOfWeek, addDays, isBefore, isWithinInterval,
  startOfDay, differenceInDays, parseISO
} from "date-fns";
import { fr } from "date-fns/locale";
import {
  CalendarDays, AlertCircle, CheckSquare, Bot, Users, Wrench,
  Warehouse, List, LayoutGrid,
  Package, MapPin, GraduationCap, FileText, Hammer, PlayCircle, Cog
} from "lucide-react";
import { cn } from "@/lib/utils";
import { compareMissions, resolveMissionTitle } from "@/lib/mission-display";
import { isRobotFleetAvailable } from "@shared/robotStatusConfig";

// ─── helpers ──────────────────────────────────────────────────────────────────

const MISSION_TYPE_LABELS: Record<string, string> = {
  installation: "Installation",
  demonstration: "Démonstration",
  sav: "SAV",
  livraison: "Livraison",
  formation: "Formation",
  cahier_des_charges: "Cahier des charges",
  salon: "Salon",
  atelier: "Atelier",
};

const MISSION_TYPE_ICONS: Record<string, React.ReactNode> = {
  installation:        <Cog className="w-3.5 h-3.5" />,
  demonstration:       <PlayCircle className="w-3.5 h-3.5" />,
  sav:                 <Wrench className="w-3.5 h-3.5" />,
  livraison:           <Package className="w-3.5 h-3.5" />,
  formation:           <GraduationCap className="w-3.5 h-3.5" />,
  cahier_des_charges:  <FileText className="w-3.5 h-3.5" />,
  salon:               <MapPin className="w-3.5 h-3.5" />,
  atelier:             <Hammer className="w-3.5 h-3.5" />,
};

const PRIORITY_ORDER: Record<string, number> = {
  urgent: 0, eleve: 1, moyen: 2, faible: 3,
};

const PRIORITY_LABELS: Record<string, { label: string; color: string }> = {
  urgent: { label: "Critique", color: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400" },
  eleve:  { label: "Haute",    color: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400" },
  moyen:  { label: "Normale",  color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400" },
  faible: { label: "Basse",    color: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400" },
};

const TASK_PRIORITY_LABELS: Record<string, { label: string; color: string }> = {
  "1": { label: "Haute",    color: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400" },
  "2": { label: "Normale",  color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400" },
  "3": { label: "Basse",    color: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400" },
};

const SCHEDULE_STATUS: Record<string, { label: string; color: string }> = {
  bureau:      { label: "Bureau",      color: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400" },
  remote:      { label: "À distance",  color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400" },
  absent:      { label: "Absent",      color: "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400" },
  conges:      { label: "Congés",      color: "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400" },
  alternance:  { label: "Alternance",  color: "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400" },
};

const SAV_STATUS_LABELS: Record<string, string> = {
  ouvert:               "Ouvert",
  en_cours:             "En cours",
  en_attente_pieces:    "En attente pièces",
  resolu:               "Résolu",
  cloture:              "Clôturé",
};

function getWeekDays(monday: Date) {
  return Array.from({ length: 5 }, (_, i) => addDays(monday, i));
}


// ─── avatar helpers ────────────────────────────────────────────────────────────

const AVATAR_COLORS = [
  "bg-blue-500", "bg-emerald-500", "bg-violet-500", "bg-orange-500",
  "bg-pink-500", "bg-teal-500", "bg-rose-500", "bg-cyan-500",
];

function nameToColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) | 0;
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function AvatarSmall({ profile, style }: { profile: any; style?: React.CSSProperties }) {
  if (!profile) return null;
  const fullName = `${profile.firstName ?? ""} ${profile.lastName ?? ""}`.trim();
  const initials = `${(profile.firstName ?? "")[0] ?? ""}${(profile.lastName ?? "")[0] ?? ""}`.toUpperCase() || "?";
  const base = "w-8 h-8 rounded-full border-2 border-white dark:border-background object-cover shrink-0 flex items-center justify-center";
  if (profile.avatarUrl) {
    return <img src={profile.avatarUrl} alt={fullName} title={fullName} className={`${base} object-cover`} style={style} />;
  }
  return (
    <div title={fullName} className={`${base} ${nameToColor(fullName)} text-white text-[10px] font-bold`} style={style}>
      {initials}
    </div>
  );
}

function AvatarGroup({ ids, allProfiles }: { ids: string[]; allProfiles: any[] }) {
  if (!ids.length) return null;
  const profiles = ids.slice(0, 3).map((id) => allProfiles.find((p) => p.id === id)).filter(Boolean);
  const extra = Math.max(0, ids.length - 3);
  return (
    <div className="flex items-center">
      {profiles.map((p, i) => (
        <div key={p.id} style={{ marginLeft: i === 0 ? 0 : -8, zIndex: profiles.length - i }} className="relative">
          <AvatarSmall profile={p} />
        </div>
      ))}
      {extra > 0 && (
        <div
          className="w-8 h-8 rounded-full bg-slate-300 dark:bg-slate-600 border-2 border-white dark:border-background flex items-center justify-center text-[10px] font-bold text-slate-700 dark:text-slate-200 shrink-0"
          style={{ marginLeft: -8, zIndex: 0 }}
        >
          +{extra}
        </div>
      )}
    </div>
  );
}

// ─── view toggle ──────────────────────────────────────────────────────────────

function useBlockView(key: string): [boolean, () => void] {
  const storageKey = `dashboard_bloc_${key}_view`;
  const [isCard, setIsCard] = useState<boolean>(() => {
    try { return localStorage.getItem(storageKey) === "card"; } catch { return false; }
  });
  const toggle = () => {
    const next = !isCard;
    setIsCard(next);
    try { localStorage.setItem(storageKey, next ? "card" : "list"); } catch {}
  };
  return [isCard, toggle];
}

function ViewToggle({ isCard, onToggle }: { isCard: boolean; onToggle: () => void }) {
  return (
    <button
      onClick={onToggle}
      title={isCard ? "Vue liste" : "Vue cartes"}
      className="p-1.5 rounded-md hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
    >
      {isCard ? <List className="w-4 h-4" /> : <LayoutGrid className="w-4 h-4" />}
    </button>
  );
}

// ─── sub-components ────────────────────────────────────────────────────────────

function BlockSkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div className="space-y-3">
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} className="h-14 w-full rounded-lg" />
      ))}
    </div>
  );
}

function EmptyMsg({ children, green }: { children: React.ReactNode; green?: boolean }) {
  return (
    <p className={cn("text-sm text-center py-6", green ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground")}>
      {children}
    </p>
  );
}

function ErrorMsg({ msg }: { msg: string }) {
  return (
    <p className="text-sm text-destructive/70 text-center py-4">{msg}</p>
  );
}

// ─── main component ────────────────────────────────────────────────────────────

export default function Dashboard() {
  const queryClient = useQueryClient();

  // Auto-refresh every 5 minutes
  useEffect(() => {
    const id = setInterval(() => {
      queryClient.invalidateQueries({ queryKey: ["/api/missions"] });
      queryClient.invalidateQueries({ queryKey: ["/api/tickets"] });
      queryClient.invalidateQueries({ queryKey: ["/api/tasks"] });
      queryClient.invalidateQueries({ queryKey: ["/api/robots"] });
      queryClient.invalidateQueries({ queryKey: ["/api/sav/dossiers"] });
      queryClient.invalidateQueries({ queryKey: ["/api/schedules"] });
    }, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [queryClient]);

  // Block view toggles (persisted)
  const [bloc1Card, toggleBloc1] = useBlockView("1");
  const [bloc2Card, toggleBloc2] = useBlockView("2");
  const [bloc3Card, toggleBloc3] = useBlockView("3");
  const [bloc4Card, toggleBloc4] = useBlockView("4");
  const [bloc6Card, toggleBloc6] = useBlockView("6");
  const [bloc8Card, toggleBloc8] = useBlockView("8");

  const today = startOfDay(new Date());
  const mondayStr = getWeekStartString(today);
  const monday = startOfWeek(today, { weekStartsOn: 1 });
  const sunday = addDays(monday, 6);
  const weekDays = getWeekDays(monday);
  const alternanceMap = useAlternanceJours(
    format(monday, "yyyy-MM-dd"),
    format(addDays(monday, 4), "yyyy-MM-dd")
  );

  // Data fetches
  const { data: missions, isLoading: loadingMissions, isError: errorMissions } = useMissions();
  const { data: tickets, isLoading: loadingTickets, isError: errorTickets } = useTickets();
  const { data: tasks, isLoading: loadingTasks, isError: errorTasks } = useTasks();
  const { data: robots, isLoading: loadingRobots, isError: errorRobots } = useRobots();
  const { data: savDossiers, isLoading: loadingSav, isError: errorSav } = useSavDossiers();
  const { data: schedules, isLoading: loadingSchedules } = useTechnicianSchedules();
  const { data: technicians } = useTechnicians();
  const { data: commercials } = useCommercials();
  const { data: clients } = useClients();
  const { data: prospects } = useProspects();
  const { data: resellers } = useResellers();

  // All profiles for avatars
  const allProfiles: any[] = [
    ...(technicians as any[] || []),
    ...(commercials as any[] || []),
  ];

  // Lookup helpers
  const clientName = (id: string | null) => {
    if (!id) return null;
    const c = (clients as any[])?.find((c: any) => c.id === id);
    return c?.nomCommercial ?? c?.companyName ?? c?.societe ?? null;
  };
  const prospectName = (id: string | null) => {
    if (!id) return null;
    return (prospects as any[])?.find((p: any) => p.id === id)?.societe ?? null;
  };
  const resellerName = (id: string | null) => {
    if (!id) return null;
    const r = (resellers as any[])?.find((r: any) => r.id === id);
    return r?.nomCommercial ?? r?.companyName ?? null;
  };
  const profileName = (id: string | null) => {
    const p = allProfiles.find((x) => x.id === id);
    return p ? `${p.firstName ?? ""} ${p.lastName ?? ""}`.trim() || p.firstName : null;
  };
  const missionEntityName = (m: any) =>
    clientName(m.clientId) ?? prospectName(m.prospectId) ?? "—";

  // ── BLOC 1 — Missions ──────────────────────────────────────────────────────

  const todayStr = format(today, "yyyy-MM-dd");

  const missionsToday = (missions || []).filter((m: any) => {
    const start = m.startDate?.substring(0, 10);
    const end = m.endDate?.substring(0, 10);
    return start <= todayStr && end >= todayStr && m.status !== "annule";
  }).sort(compareMissions);

  const mondayStrEnd = format(sunday, "yyyy-MM-dd");
  const missionsWeek = (missions || []).filter((m: any) => {
    const start = m.startDate?.substring(0, 10);
    const end = m.endDate?.substring(0, 10);
    return start <= mondayStrEnd && end >= mondayStr && m.status !== "annule";
  });

  // Group by day for week view
  const missionsByDay = weekDays.map((day) => {
    const dayStr = format(day, "yyyy-MM-dd");
    return {
      day,
      dayStr,
      missions: missionsWeek.filter((m: any) => {
        const start = m.startDate?.substring(0, 10);
        const end = m.endDate?.substring(0, 10);
        return start <= dayStr && end >= dayStr;
      }).sort(compareMissions),
    };
  });

  // ── BLOC 2 — Tickets ouverts ───────────────────────────────────────────────

  const openTickets = (tickets as any[] || [])
    .filter((t: any) => t.status !== "resolu" && t.status !== "ferme" && !t.archive)
    .sort((a: any, b: any) => {
      const pa = PRIORITY_ORDER[a.priority] ?? 99;
      const pb = PRIORITY_ORDER[b.priority] ?? 99;
      if (pa !== pb) return pa - pb;
      return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    });

  // ── BLOC 3 — Tâches en retard ─────────────────────────────────────────────

  const overdueTasks = (tasks as any[] || []).filter((t: any) => {
    if (!t.dateEcheance) return false;
    if (t.status === "fait" || t.archive) return false;
    return isBefore(parseISO(t.dateEcheance), today);
  });

  // ── BLOC 4 — Disponibilité robots ─────────────────────────────────────────

  const availableRobots = (robots as any[] || []).filter((r: any) => isRobotFleetAvailable(r.status));
  const busyRobots = (robots as any[] || []).filter((r: any) => !isRobotFleetAvailable(r.status));

  // link robots to active missions
  const robotMissionMap: Record<string, string> = {};
  (missions as any[] || []).filter((m: any) => m.status === "en_cours" || m.status === "confirme").forEach((m: any) => {
    (m.robotIds || []).forEach((rid: string) => {
      robotMissionMap[rid] = m.eventName || MISSION_TYPE_LABELS[m.missionType] || "Mission";
    });
  });

  // link robots to open SAV
  const robotSavMap: Record<string, string> = {};
  (savDossiers as any[] || []).filter((d: any) => d.statut !== "cloture").forEach((d: any) => {
    if (d.robotId) robotSavMap[d.robotId] = d.reference;
  });

  // ── BLOC 5 — Planning équipe ───────────────────────────────────────────────

  const teamMembers = [
    ...(technicians as any[] || []).map((t: any) => ({ ...t, roleLabel: "Technicien" })),
    ...(commercials as any[] || []).map((c: any) => ({ ...c, roleLabel: "Commercial" })),
  ].filter((m: any) => m.isActive !== false);

  const scheduleMap: Record<string, any> = {};
  (schedules as any[] || []).filter((s: any) => s.weekStart === mondayStr).forEach((s: any) => {
    scheduleMap[s.technicianId] = s;
  });

  // ── BLOC 7 — Alertes stock ────────────────────────────────────────────────

  const { data: stockAlerts, isLoading: loadingStock } = useQuery<{ rupture: number; alerte: number; top5: any[] }>({
    queryKey: ["/api/stock/alerts"],
    queryFn: async () => {
      const r = await authFetch("/api/stock/alerts");
      return r.json();
    },
    staleTime: 5 * 60 * 1000,
  });

  // ── BLOC 6 — Dossiers SAV anciens ─────────────────────────────────────────

  const sevenDaysAgo = addDays(today, -7);
  const oldSavDossiers = (savDossiers as any[] || [])
    .filter((d: any) => {
      if (d.statut === "cloture") return false;
      const created = parseISO(d.createdAt);
      return isBefore(created, sevenDaysAgo);
    })
    .sort((a: any, b: any) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

  // ─────────────────────────────────────────────────────────────────────────────

  // Card view renderers for missions list
  const renderMissionsCard = (missionList: any[]) => (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
      {missionList.map((m: any) => {
        const techIds: string[] = m.technicianIds || [];
        const entity = missionEntityName(m);
        const title = resolveMissionTitle(m, entity === "—" ? null : entity);
        const icon = MISSION_TYPE_ICONS[m.missionType] ?? <CalendarDays className="w-3.5 h-3.5" />;
        return (
          <Link key={m.id} href="/missions">
            <div className={`flex flex-col justify-between p-3.5 rounded-xl border bg-secondary/20 hover:bg-secondary/40 cursor-pointer transition-colors h-full min-h-[110px] gap-2 ${m.isMine ? "border-l-[3px] border-l-[hsl(var(--accent-mine))]" : ""}`} data-testid={`mission-card-${m.id}`}>
              <div className="space-y-1">
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground font-medium">
                  {icon}
                  <span>{MISSION_TYPE_LABELS[m.missionType] ?? m.missionType}</span>
                </div>
                <p className="font-semibold text-sm leading-snug truncate" title={title}>{title}</p>
                {m.address && (
                  <p className="text-xs text-muted-foreground truncate">{m.address}</p>
                )}
                {(m.arrivalTime || m.heureDebut) && (
                  <p className="text-xs text-muted-foreground">
                    {m.arrivalTime && `Arrivée ${m.arrivalTime}`}
                    {m.arrivalTime && m.heureDebut && " · "}
                    {m.heureDebut && `Début ${m.heureDebut}`}
                  </p>
                )}
              </div>
              <div className="flex items-center justify-between gap-2 pt-1">
                <StatusBadge status={m.status} type="mission" />
                {techIds.length > 0 && <AvatarGroup ids={techIds} allProfiles={allProfiles} />}
              </div>
            </div>
          </Link>
        );
      })}
    </div>
  );

  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div>
        <h1 className="text-2xl font-bold">Tableau de bord</h1>
        <p className="text-muted-foreground text-sm mt-0.5">
          {format(today, "EEEE d MMMM yyyy", { locale: fr }).replace(/^\w/, c => c.toUpperCase())}
        </p>
      </div>

      {/* ── BLOC 1 — MISSIONS ────────────────────────────────────────────── */}
      <Card className="shadow-sm border-border/60">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center justify-between text-base">
            <span className="flex items-center gap-2">
              <CalendarDays className="w-4 h-4 text-primary" />
              Missions
            </span>
            <ViewToggle isCard={bloc1Card} onToggle={toggleBloc1} />
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loadingMissions ? <BlockSkeleton rows={3} /> : errorMissions ? <ErrorMsg msg="Impossible de charger les missions." /> : (
            <Tabs defaultValue="today">
              <TabsList className="mb-4">
                <TabsTrigger value="today" data-testid="tab-missions-today">
                  Aujourd'hui
                  {missionsToday.length > 0 && (
                    <span className="ml-1.5 bg-primary text-primary-foreground text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                      {missionsToday.length}
                    </span>
                  )}
                </TabsTrigger>
                <TabsTrigger value="week" data-testid="tab-missions-week">Cette semaine</TabsTrigger>
              </TabsList>

              <TabsContent value="today">
                {missionsToday.length === 0 ? (
                  <EmptyMsg>Aucune mission prévue aujourd'hui.</EmptyMsg>
                ) : bloc1Card ? (
                  renderMissionsCard(missionsToday)
                ) : (
                  <div className="space-y-2">
                    {missionsToday.map((m: any) => {
                      const techIds: string[] = m.technicianIds || [];
                      const title = resolveMissionTitle(m, missionEntityName(m));
                      return (
                        <Link key={m.id} href="/missions">
                          <div className={`flex items-center justify-between p-3 rounded-lg border bg-secondary/20 hover:bg-secondary/40 cursor-pointer transition-colors ${m.isMine ? "border-l-[3px] border-l-[hsl(var(--accent-mine))]" : ""}`} data-testid={`mission-today-${m.id}`}>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate" title={title}>{title}</p>
                              <p className="text-xs text-muted-foreground truncate">
                                {missionEntityName(m)}
                                {m.arrivalTime && ` · Arrivée ${m.arrivalTime}`}
                                {m.heureDebut && ` · Début ${m.heureDebut}`}
                              </p>
                            </div>
                            <div className="flex items-center gap-2 ml-3 shrink-0">
                              {techIds.length > 0 && <AvatarGroup ids={techIds} allProfiles={allProfiles} />}
                              <StatusBadge status={m.status} type="mission" />
                            </div>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="week">
                {bloc1Card ? (
                  <div className="space-y-6">
                    {missionsByDay.map(({ day, dayStr, missions: dayMissions }) => (
                      <div key={dayStr}>
                        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                          {format(day, "EEEE d MMMM", { locale: fr }).replace(/^\w/, c => c.toUpperCase())}
                        </p>
                        {dayMissions.length === 0 ? (
                          <p className="text-xs text-muted-foreground italic pl-2">Aucune mission</p>
                        ) : renderMissionsCard(dayMissions)}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {missionsByDay.map(({ day, dayStr, missions: dayMissions }) => (
                      <div key={dayStr}>
                        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">
                          {format(day, "EEEE d MMMM", { locale: fr }).replace(/^\w/, c => c.toUpperCase())}
                        </p>
                        {dayMissions.length === 0 ? (
                          <p className="text-xs text-muted-foreground italic pl-2">Aucune mission</p>
                        ) : (
                          <div className="space-y-1.5">
                            {dayMissions.map((m: any) => {
                              const techIds: string[] = m.technicianIds || [];
                              const title = resolveMissionTitle(m, missionEntityName(m));
                              return (
                                <Link key={m.id} href="/missions">
                                  <div className={`flex items-center justify-between p-2.5 rounded-lg border bg-secondary/20 hover:bg-secondary/40 cursor-pointer transition-colors ${m.isMine ? "border-l-[3px] border-l-[hsl(var(--accent-mine))]" : ""}`} data-testid={`mission-week-${m.id}`}>
                                    <div className="flex-1 min-w-0">
                                      <p className="font-medium text-sm truncate" title={title}>{title}</p>
                                      <p className="text-xs text-muted-foreground truncate">{missionEntityName(m)}</p>
                                    </div>
                                    <div className="flex items-center gap-2 ml-2 shrink-0">
                                      {techIds.length > 0 && <AvatarGroup ids={techIds} allProfiles={allProfiles} />}
                                      <StatusBadge status={m.status} type="mission" />
                                    </div>
                                  </div>
                                </Link>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          )}
        </CardContent>
      </Card>

      {/* ── BLOC 2 — TICKETS OUVERTS ─────────────────────────────────────── */}
      <Card className="shadow-sm border-border/60">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center justify-between text-base">
            <span className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-orange-500" />
              Tickets ouverts
            </span>
            <div className="flex items-center gap-2">
              <span className="text-sm font-normal text-muted-foreground">
                {loadingTickets ? "…" : `${openTickets.length} ticket${openTickets.length !== 1 ? "s" : ""}`}
              </span>
              <ViewToggle isCard={bloc2Card} onToggle={toggleBloc2} />
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loadingTickets ? <BlockSkeleton rows={4} /> : errorTickets ? <ErrorMsg msg="Impossible de charger les tickets." /> : openTickets.length === 0 ? (
            <EmptyMsg green>Aucun ticket ouvert.</EmptyMsg>
          ) : bloc2Card ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                {openTickets.slice(0, 9).map((t: any) => {
                  const techProfile = allProfiles.find((p) => p.id === t.assignedTechnicianId);
                  return (
                    <Link key={t.id} href="/tickets">
                      <div className={`flex flex-col justify-between p-3.5 rounded-xl border bg-secondary/20 hover:bg-secondary/40 cursor-pointer transition-colors min-h-[120px] gap-2 ${t.isMine ? "border-l-[3px] border-l-[hsl(var(--accent-mine))]" : ""}`} data-testid={`ticket-card-${t.id}`}>
                        <div className="space-y-1">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="font-mono text-[11px] font-semibold text-primary">{t.ticketNumber}</span>
                            {PRIORITY_LABELS[t.priority] && (
                              <Badge className={cn("text-[10px] px-1.5 py-0 font-semibold", PRIORITY_LABELS[t.priority].color)} variant="outline">
                                {PRIORITY_LABELS[t.priority].label}
                              </Badge>
                            )}
                          </div>
                          <p className="font-semibold text-sm leading-snug line-clamp-2">
                            {clientName(t.clientId) ?? resellerName(t.resellerId) ?? "—"}
                          </p>
                          {t.problemDescription && (
                            <p className="text-xs text-muted-foreground line-clamp-2">{t.problemDescription}</p>
                          )}
                        </div>
                        <div className="flex items-center justify-between gap-2 pt-1">
                          <div className="flex items-center gap-2">
                            <StatusBadge status={t.status} type="ticket" />
                            {t.createdAt && (
                              <span className="text-[10px] text-muted-foreground">
                                {format(new Date(t.createdAt), "d MMM", { locale: fr })}
                              </span>
                            )}
                          </div>
                          {techProfile && <AvatarSmall profile={techProfile} />}
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
              {openTickets.length > 9 && (
                <p className="text-xs text-muted-foreground text-center pt-3">+ {openTickets.length - 9} autres tickets</p>
              )}
            </>
          ) : (
            <>
              <div className="space-y-2">
                {openTickets.slice(0, 10).map((t: any) => {
                  const techProfile = allProfiles.find((p) => p.id === t.assignedTechnicianId);
                  return (
                    <Link key={t.id} href="/tickets">
                      <div className={`flex items-start justify-between p-3 rounded-lg border bg-secondary/20 hover:bg-secondary/40 cursor-pointer transition-colors ${t.isMine ? "border-l-[3px] border-l-[hsl(var(--accent-mine))]" : ""}`} data-testid={`ticket-open-${t.id}`}>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-mono text-xs font-semibold text-primary">{t.ticketNumber}</span>
                            {PRIORITY_LABELS[t.priority] && (
                              <Badge className={cn("text-[10px] px-1.5 py-0 font-semibold", PRIORITY_LABELS[t.priority].color)} variant="outline">
                                {PRIORITY_LABELS[t.priority].label}
                              </Badge>
                            )}
                            <StatusBadge status={t.status} type="ticket" />
                          </div>
                          <p className="text-sm font-medium mt-0.5 truncate">{t.problemDescription || "—"}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {clientName(t.clientId) ?? resellerName(t.resellerId) ?? "—"}
                            {t.createdAt && ` · ${format(new Date(t.createdAt), "d MMM yyyy", { locale: fr })}`}
                          </p>
                        </div>
                        {techProfile && (
                          <div className="ml-3 shrink-0 mt-0.5">
                            <AvatarSmall profile={techProfile} />
                          </div>
                        )}
                      </div>
                    </Link>
                  );
                })}
              </div>
              {openTickets.length > 10 && (
                <p className="text-xs text-muted-foreground text-center pt-1">+ {openTickets.length - 10} autres tickets</p>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* ── BLOC 3 — TÂCHES EN RETARD ────────────────────────────────────── */}
      <Card className="shadow-sm border-border/60">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center justify-between text-base">
            <span className="flex items-center gap-2">
              <CheckSquare className="w-4 h-4 text-red-500" />
              Tâches en retard
            </span>
            <div className="flex items-center gap-2">
              <span className="text-sm font-normal text-muted-foreground">
                {loadingTasks ? "…" : `${overdueTasks.length} tâche${overdueTasks.length !== 1 ? "s" : ""}`}
              </span>
              <ViewToggle isCard={bloc3Card} onToggle={toggleBloc3} />
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loadingTasks ? <BlockSkeleton rows={3} /> : errorTasks ? <ErrorMsg msg="Impossible de charger les tâches." /> : overdueTasks.length === 0 ? (
            <EmptyMsg green>Aucune tâche en retard.</EmptyMsg>
          ) : bloc3Card ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                {overdueTasks.slice(0, 9).map((t: any) => {
                  const assigneeProfile = allProfiles.find((p) => p.id === t.assigneeId);
                  return (
                    <Link key={t.id} href="/tasks">
                      <div className="flex flex-col justify-between p-3.5 rounded-xl border bg-secondary/20 hover:bg-secondary/40 cursor-pointer transition-colors min-h-[100px] gap-2" data-testid={`task-card-${t.id}`}>
                        <p className="font-semibold text-sm leading-snug line-clamp-3">{t.titre}</p>
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            {TASK_PRIORITY_LABELS[t.priorite] && (
                              <Badge className={cn("text-[10px] px-1.5 py-0 font-semibold", TASK_PRIORITY_LABELS[t.priorite].color)} variant="outline">
                                {TASK_PRIORITY_LABELS[t.priorite].label}
                              </Badge>
                            )}
                            {t.dateEcheance && (
                              <span className="text-xs font-bold text-red-600 dark:text-red-400">
                                {format(parseISO(t.dateEcheance), "d MMM", { locale: fr })}
                              </span>
                            )}
                          </div>
                          {assigneeProfile && <AvatarSmall profile={assigneeProfile} />}
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
              {overdueTasks.length > 9 && (
                <p className="text-xs text-muted-foreground text-center pt-3">+ {overdueTasks.length - 9} autres tâches</p>
              )}
            </>
          ) : (
            <>
              <div className="space-y-2">
                {overdueTasks.slice(0, 10).map((t: any) => {
                  const assigneeProfile = allProfiles.find((p) => p.id === t.assigneeId);
                  return (
                    <Link key={t.id} href="/tasks">
                      <div className="flex items-center justify-between p-3 rounded-lg border bg-secondary/20 hover:bg-secondary/40 cursor-pointer transition-colors" data-testid={`task-overdue-${t.id}`}>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{t.titre}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {t.assigneeId ? (profileName(t.assigneeId) ?? "—") : "Non assigné"}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 ml-3 shrink-0">
                          {TASK_PRIORITY_LABELS[t.priorite] && (
                            <Badge className={cn("text-[10px] px-1.5 py-0 font-semibold", TASK_PRIORITY_LABELS[t.priorite].color)} variant="outline">
                              {TASK_PRIORITY_LABELS[t.priorite].label}
                            </Badge>
                          )}
                          {t.dateEcheance && (
                            <span className="text-xs font-medium text-red-600 dark:text-red-400">
                              {format(parseISO(t.dateEcheance), "d MMM", { locale: fr })}
                            </span>
                          )}
                          {assigneeProfile && <AvatarSmall profile={assigneeProfile} />}
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
              {overdueTasks.length > 10 && (
                <p className="text-xs text-muted-foreground text-center pt-1">+ {overdueTasks.length - 10} autres tâches</p>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* ── BLOC 4 — DISPONIBILITÉ ROBOTS ────────────────────────────────── */}
      <Card className="shadow-sm border-border/60">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center justify-between text-base">
            <span className="flex items-center gap-2">
              <Bot className="w-4 h-4 text-violet-500" />
              Flotte robots
            </span>
            {busyRobots.length > 0 && <ViewToggle isCard={bloc4Card} onToggle={toggleBloc4} />}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loadingRobots ? <BlockSkeleton rows={2} /> : errorRobots ? <ErrorMsg msg="Impossible de charger les robots." /> : (
            <div className="space-y-4">
              {/* KPIs — toujours affichés */}
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-xl border bg-emerald-50 dark:bg-emerald-900/10 p-4 text-center">
                  <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{availableRobots.length}</p>
                  <p className="text-xs text-emerald-700 dark:text-emerald-500 mt-0.5 font-medium">Disponibles</p>
                </div>
                <div className="rounded-xl border bg-amber-50 dark:bg-amber-900/10 p-4 text-center">
                  <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">{busyRobots.length}</p>
                  <p className="text-xs text-amber-700 dark:text-amber-500 mt-0.5 font-medium">Indisponibles</p>
                </div>
              </div>
              {busyRobots.length > 0 && (
                <>
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Robots non disponibles</p>
                  {bloc4Card ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3">
                      {busyRobots.map((r: any) => (
                        <div key={r.id} className="flex flex-col gap-2 p-3 rounded-xl border bg-secondary/20" data-testid={`robot-card-${r.id}`}>
                          {(r.imageUrl || r.profilePhotoUrl) ? (
                            <img src={r.imageUrl || r.profilePhotoUrl} alt={r.modelName} className="w-full aspect-video rounded-lg object-cover border border-border" />
                          ) : (
                            <div className="w-full aspect-video rounded-lg bg-muted flex items-center justify-center border border-border">
                              <Bot className="w-6 h-6 text-muted-foreground" />
                            </div>
                          )}
                          <div>
                            <p className="text-sm font-semibold truncate">{r.nickname || r.modelName}</p>
                            {(robotMissionMap[r.id] || robotSavMap[r.id]) && (
                              <p className="text-xs text-muted-foreground truncate">
                                {robotMissionMap[r.id] ? `Mission : ${robotMissionMap[r.id]}` : `SAV : ${robotSavMap[r.id]}`}
                              </p>
                            )}
                          </div>
                          <StatusBadge status={r.status} type="robot" />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-1.5">
                      {busyRobots.map((r: any) => (
                        <div key={r.id} className="flex items-center gap-2 justify-between p-2.5 rounded-lg border bg-secondary/20" data-testid={`robot-busy-${r.id}`}>
                          {(r.imageUrl || r.profilePhotoUrl) ? (
                            <img src={r.imageUrl || r.profilePhotoUrl} alt={r.modelName} className="w-8 h-8 rounded-md object-cover border border-border shrink-0" />
                          ) : (
                            <div className="w-8 h-8 rounded-md bg-muted flex items-center justify-center shrink-0">
                              <Bot className="w-4 h-4 text-muted-foreground" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{r.nickname || r.modelName}</p>
                            {(robotMissionMap[r.id] || robotSavMap[r.id]) && (
                              <p className="text-xs text-muted-foreground truncate">
                                {robotMissionMap[r.id] ? `Mission : ${robotMissionMap[r.id]}` : `SAV : ${robotSavMap[r.id]}`}
                              </p>
                            )}
                          </div>
                          <StatusBadge status={r.status} type="robot" />
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── BLOC 5 — PLANNING ÉQUIPE ─────────────────────────────────────── */}
      <Card className="shadow-sm border-border/60">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Users className="w-4 h-4 text-blue-500" />
            Planning équipe — semaine du {format(monday, "d MMMM", { locale: fr })}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loadingSchedules ? <BlockSkeleton rows={4} /> : (
            teamMembers.length === 0 ? (
              <EmptyMsg>Aucun collaborateur actif trouvé.</EmptyMsg>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr>
                      <th className="text-left text-xs font-semibold text-muted-foreground pb-2 pr-4 min-w-[140px]">Collaborateur</th>
                      {weekDays.map((day) => (
                        <th key={day.toISOString()} className="text-center text-xs font-semibold text-muted-foreground pb-2 px-1 min-w-[90px]">
                          {format(day, "EEE d", { locale: fr }).replace(/^\w/, c => c.toUpperCase())}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/30">
                    {teamMembers.map((member: any) => {
                      const sched = scheduleMap[member.id];
                      return (
                        <tr key={member.id} data-testid={`schedule-row-${member.id}`}>
                          <td className="py-2 pr-4">
                            <div className="flex items-center gap-2">
                              <AvatarSmall profile={member} />
                              <div>
                                <p className="font-medium text-sm leading-tight">
                                  {member.firstName} {member.lastName}
                                </p>
                                <p className="text-xs text-muted-foreground">{member.roleLabel}</p>
                              </div>
                            </div>
                          </td>
                          {weekDays.map((day, i) => {
                            const dateISO = format(day, "yyyy-MM-dd");
                            const altStatut = alternanceMap.get(`${member.id}_${dateISO}`);
                            const status = resolveDayStatus(sched, DAY_STATUS_KEYS[i], altStatut);
                            const info = SCHEDULE_STATUS[status];
                            return (
                              <td key={i} className="py-2 px-1 text-center">
                                <Badge className={cn("text-[10px] px-1.5 py-0.5 font-medium", info?.color)} variant="outline">
                                  {info?.label ?? status}
                                </Badge>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )
          )}
        </CardContent>
      </Card>

      {/* ── BLOC 6 — DOSSIERS SAV ANCIENS ────────────────────────────────── */}
      <Card className="shadow-sm border-border/60">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center justify-between text-base">
            <span className="flex items-center gap-2">
              <Wrench className="w-4 h-4 text-red-500" />
              Dossiers SAV en attente &gt; 7 jours
            </span>
            <div className="flex items-center gap-2">
              <span className="text-sm font-normal text-muted-foreground">
                {loadingSav ? "…" : `${oldSavDossiers.length} dossier${oldSavDossiers.length !== 1 ? "s" : ""}`}
              </span>
              {oldSavDossiers.length > 0 && <ViewToggle isCard={bloc6Card} onToggle={toggleBloc6} />}
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loadingSav ? <BlockSkeleton rows={3} /> : errorSav ? <ErrorMsg msg="Impossible de charger les dossiers SAV." /> : oldSavDossiers.length === 0 ? (
            <EmptyMsg green>Aucun dossier SAV en attente depuis plus de 7 jours.</EmptyMsg>
          ) : bloc6Card ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
              {oldSavDossiers.map((d: any) => {
                const days = differenceInDays(today, parseISO(d.createdAt));
                const isOld = days > 14;
                const techProfile = allProfiles.find((p) => p.id === d.technicienReferentId);
                return (
                  <Link key={d.id} href="/sav">
                    <div className="flex flex-col justify-between p-3.5 rounded-xl border bg-secondary/20 hover:bg-secondary/40 cursor-pointer transition-colors min-h-[110px] gap-2" data-testid={`sav-card-${d.id}`}>
                      <div className="space-y-1">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="font-mono text-[11px] font-semibold text-primary">{d.reference}</span>
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0 font-medium text-muted-foreground">
                            {SAV_STATUS_LABELS[d.statut] ?? d.statut}
                          </Badge>
                        </div>
                        {d.problematique && (
                          <p className="text-sm font-medium line-clamp-2 leading-snug">{d.problematique}</p>
                        )}
                        <p className="text-xs text-muted-foreground truncate">{clientName(d.clientId) ?? "—"}</p>
                      </div>
                      <div className="flex items-center justify-between gap-2 pt-1">
                        <span className={cn("text-sm font-bold", isOld ? "text-red-600 dark:text-red-400" : "text-amber-600 dark:text-amber-400")}>
                          {days} jour{days > 1 ? "s" : ""}
                        </span>
                        {techProfile && <AvatarSmall profile={techProfile} />}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          ) : (
            <div className="space-y-2">
              {oldSavDossiers.map((d: any) => {
                const days = differenceInDays(today, parseISO(d.createdAt));
                const isOld = days > 14;
                const techProfile = allProfiles.find((p) => p.id === d.technicienReferentId);
                return (
                  <Link key={d.id} href="/sav">
                    <div className="flex items-center justify-between p-3 rounded-lg border bg-secondary/20 hover:bg-secondary/40 cursor-pointer transition-colors" data-testid={`sav-old-${d.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs font-semibold text-primary">{d.reference}</span>
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0 font-medium text-muted-foreground">
                            {SAV_STATUS_LABELS[d.statut] ?? d.statut}
                          </Badge>
                        </div>
                        <p className="text-sm font-medium truncate mt-0.5">{d.problematique}</p>
                        <p className="text-xs text-muted-foreground truncate mt-0.5">
                          {clientName(d.clientId) ?? "—"}
                          {d.technicienReferentId && ` · ${profileName(d.technicienReferentId) ?? "—"}`}
                        </p>
                      </div>
                      <div className="ml-3 shrink-0 flex items-center gap-2">
                        {techProfile && <AvatarSmall profile={techProfile} />}
                        <span className={cn("text-xs font-semibold", isOld ? "text-red-600 dark:text-red-400" : "text-amber-600 dark:text-amber-400")}>
                          {days}j
                        </span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── BLOC 8 — ALERTES STOCK ────────────────────────────────────────── */}
      {(loadingStock || (stockAlerts && (stockAlerts.rupture > 0 || stockAlerts.alerte > 0))) && (
        <Card className="shadow-sm border-border/60">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center justify-between text-base">
              <span className="flex items-center gap-2">
                <Warehouse className="w-4 h-4 text-orange-500" />
                Alertes stock
              </span>
              <div className="flex items-center gap-2">
                <Link href="/stock?filtre=alerte">
                  <span className="text-xs font-normal text-primary hover:underline cursor-pointer">Voir tout →</span>
                </Link>
                {stockAlerts && stockAlerts.top5.length > 0 && <ViewToggle isCard={bloc8Card} onToggle={toggleBloc8} />}
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingStock ? <BlockSkeleton rows={3} /> : !stockAlerts ? null : (
              <div className="space-y-3">
                {/* KPIs rupture / alerte — toujours affichés */}
                <div className="flex gap-3">
                  {stockAlerts.rupture > 0 && (
                    <div className="flex-1 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-red-600 dark:text-red-400">{stockAlerts.rupture}</p>
                      <p className="text-xs text-red-600 dark:text-red-400 font-medium">Rupture{stockAlerts.rupture > 1 ? "s" : ""}</p>
                    </div>
                  )}
                  {stockAlerts.alerte > 0 && (
                    <div className="flex-1 bg-orange-50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-800 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-orange-500">{stockAlerts.alerte}</p>
                      <p className="text-xs text-orange-500 font-medium">En alerte{stockAlerts.alerte > 1 ? "s" : ""}</p>
                    </div>
                  )}
                </div>
                {/* Références critiques */}
                {stockAlerts.top5.length > 0 && (
                  <>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Références critiques</p>
                    {bloc8Card ? (
                      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-2">
                        {stockAlerts.top5.map((s: any) => {
                          const dispo = (s.stockPhysique || 0) + (s.stockAVenir || 0) - (s.stockDemo || 0) - (s.stockTampon || 0);
                          return (
                            <Link key={s.id} href="/stock">
                              <div className="flex flex-col items-center gap-2 p-3 rounded-xl border border-border/40 bg-secondary/10 hover:bg-secondary/30 cursor-pointer transition-colors text-center" data-testid={`stock-card-${s.id}`}>
                                {s.imageUrl ? (
                                  <img src={s.imageUrl} alt={s.nom} className="w-10 h-10 rounded-lg object-cover border border-border" />
                                ) : (
                                  <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center border border-border">
                                    <Warehouse className="w-5 h-5 text-muted-foreground" />
                                  </div>
                                )}
                                <div>
                                  <p className="text-xs font-medium line-clamp-2 leading-snug">{s.nom}</p>
                                  <p className="text-[10px] text-muted-foreground font-mono mt-0.5">{s.sku}</p>
                                </div>
                                <span className={cn("font-mono font-bold text-sm", dispo <= 0 ? "text-red-600 dark:text-red-400" : "text-orange-500")}>
                                  {dispo}
                                </span>
                              </div>
                            </Link>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="space-y-1.5">
                        {stockAlerts.top5.map((s: any) => {
                          const dispo = (s.stockPhysique || 0) + (s.stockAVenir || 0) - (s.stockDemo || 0) - (s.stockTampon || 0);
                          return (
                            <Link key={s.id} href="/stock">
                              <div className="flex items-center justify-between p-2.5 rounded-lg border border-border/40 bg-secondary/10 hover:bg-secondary/30 cursor-pointer transition-colors" data-testid={`stock-alert-${s.id}`}>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium truncate">{s.nom}</p>
                                  <p className="text-xs text-muted-foreground font-mono">{s.sku}</p>
                                </div>
                                <div className={cn("ml-3 font-mono font-bold text-sm flex-shrink-0", dispo <= 0 ? "text-red-600 dark:text-red-400" : "text-orange-500")}>
                                  {dispo}
                                </div>
                              </div>
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
